/**
 * System Design Templates
 * Pre-built architecture patterns
 */

import type { SystemDesignDiagram, SystemComponent, SystemConnection } from './types';
import { DEFAULT_DIAGRAM_SETTINGS } from './types';

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

export interface DiagramTemplate {
  id: string;
  name: string;
  description: string;
  category: 'microservices' | 'event-driven' | 'serverless' | 'monolith' | 'data';
  icon: string;
  createDiagram: () => Omit<SystemDesignDiagram, 'id' | 'createdAt' | 'updatedAt'>;
}

export const DIAGRAM_TEMPLATES: DiagramTemplate[] = [
  {
    id: 'microservices-basic',
    name: 'Basic Microservices',
    description: 'Simple microservices architecture with API gateway and databases',
    category: 'microservices',
    icon: '🔀',
    createDiagram: () => {
      const gatewayId = generateId();
      const userServiceId = generateId();
      const orderServiceId = generateId();
      const userDbId = generateId();
      const orderDbId = generateId();
      const clientId = generateId();

      const components: SystemComponent[] = [
        {
          id: clientId,
          type: 'client',
          name: 'Web App',
          position: { x: 100, y: 200 },
          technology: 'React',
        },
        {
          id: gatewayId,
          type: 'gateway',
          name: 'API Gateway',
          position: { x: 300, y: 200 },
          technology: 'Kong',
        },
        {
          id: userServiceId,
          type: 'service',
          name: 'User Service',
          position: { x: 550, y: 100 },
          technology: 'Node.js',
        },
        {
          id: orderServiceId,
          type: 'service',
          name: 'Order Service',
          position: { x: 550, y: 300 },
          technology: 'Python',
        },
        {
          id: userDbId,
          type: 'database',
          name: 'User DB',
          position: { x: 800, y: 100 },
          technology: 'PostgreSQL',
        },
        {
          id: orderDbId,
          type: 'database',
          name: 'Order DB',
          position: { x: 800, y: 300 },
          technology: 'PostgreSQL',
        },
      ];

      const connections: SystemConnection[] = [
        {
          id: generateId(),
          sourceId: clientId,
          targetId: gatewayId,
          connectionType: 'http',
          dataFlow: 'bidirectional',
          label: 'REST',
        },
        {
          id: generateId(),
          sourceId: gatewayId,
          targetId: userServiceId,
          connectionType: 'http',
          dataFlow: 'bidirectional',
        },
        {
          id: generateId(),
          sourceId: gatewayId,
          targetId: orderServiceId,
          connectionType: 'http',
          dataFlow: 'bidirectional',
        },
        {
          id: generateId(),
          sourceId: userServiceId,
          targetId: userDbId,
          connectionType: 'tcp',
          dataFlow: 'bidirectional',
        },
        {
          id: generateId(),
          sourceId: orderServiceId,
          targetId: orderDbId,
          connectionType: 'tcp',
          dataFlow: 'bidirectional',
        },
      ];

      return {
        name: 'Microservices Architecture',
        description: 'Basic microservices with API gateway',
        components,
        connections,
        annotations: [],
        settings: DEFAULT_DIAGRAM_SETTINGS,
      };
    },
  },
  {
    id: 'event-driven',
    name: 'Event-Driven Architecture',
    description: 'Event-driven system with message queues and async processing',
    category: 'event-driven',
    icon: '📬',
    createDiagram: () => {
      const producerId = generateId();
      const queueId = generateId();
      const consumer1Id = generateId();
      const consumer2Id = generateId();
      const dbId = generateId();
      const cacheId = generateId();

      const components: SystemComponent[] = [
        {
          id: producerId,
          type: 'service',
          name: 'Event Producer',
          position: { x: 100, y: 200 },
          technology: 'Node.js',
        },
        {
          id: queueId,
          type: 'queue',
          name: 'Event Bus',
          position: { x: 350, y: 200 },
          technology: 'Kafka',
        },
        {
          id: consumer1Id,
          type: 'service',
          name: 'Analytics Consumer',
          position: { x: 600, y: 100 },
          technology: 'Python',
        },
        {
          id: consumer2Id,
          type: 'service',
          name: 'Notification Consumer',
          position: { x: 600, y: 300 },
          technology: 'Go',
        },
        {
          id: dbId,
          type: 'database',
          name: 'Analytics DB',
          position: { x: 850, y: 100 },
          technology: 'ClickHouse',
        },
        {
          id: cacheId,
          type: 'cache',
          name: 'State Cache',
          position: { x: 850, y: 300 },
          technology: 'Redis',
        },
      ];

      const connections: SystemConnection[] = [
        {
          id: generateId(),
          sourceId: producerId,
          targetId: queueId,
          connectionType: 'pubsub',
          dataFlow: 'request',
          label: 'Publish',
        },
        {
          id: generateId(),
          sourceId: queueId,
          targetId: consumer1Id,
          connectionType: 'pubsub',
          dataFlow: 'request',
          label: 'Subscribe',
        },
        {
          id: generateId(),
          sourceId: queueId,
          targetId: consumer2Id,
          connectionType: 'pubsub',
          dataFlow: 'request',
          label: 'Subscribe',
        },
        {
          id: generateId(),
          sourceId: consumer1Id,
          targetId: dbId,
          connectionType: 'tcp',
          dataFlow: 'bidirectional',
        },
        {
          id: generateId(),
          sourceId: consumer2Id,
          targetId: cacheId,
          connectionType: 'tcp',
          dataFlow: 'bidirectional',
        },
      ];

      return {
        name: 'Event-Driven Architecture',
        description: 'Async event processing with Kafka',
        components,
        connections,
        annotations: [],
        settings: DEFAULT_DIAGRAM_SETTINGS,
      };
    },
  },
  {
    id: 'serverless',
    name: 'Serverless Architecture',
    description: 'Fully serverless with Lambda, API Gateway, and DynamoDB',
    category: 'serverless',
    icon: 'λ',
    createDiagram: () => {
      const cdnId = generateId();
      const gatewayId = generateId();
      const authFnId = generateId();
      const apiFnId = generateId();
      const processFnId = generateId();
      const dbId = generateId();
      const storageId = generateId();
      const queueId = generateId();

      const components: SystemComponent[] = [
        {
          id: cdnId,
          type: 'cdn',
          name: 'CloudFront',
          position: { x: 100, y: 200 },
          technology: 'CloudFront',
        },
        {
          id: gatewayId,
          type: 'gateway',
          name: 'API Gateway',
          position: { x: 300, y: 200 },
          technology: 'API Gateway',
        },
        {
          id: authFnId,
          type: 'function',
          name: 'Auth',
          position: { x: 500, y: 100 },
          technology: 'Lambda',
        },
        {
          id: apiFnId,
          type: 'function',
          name: 'API Handler',
          position: { x: 500, y: 200 },
          technology: 'Lambda',
        },
        {
          id: processFnId,
          type: 'function',
          name: 'Processor',
          position: { x: 500, y: 300 },
          technology: 'Lambda',
        },
        {
          id: dbId,
          type: 'database',
          name: 'DynamoDB',
          position: { x: 700, y: 150 },
          technology: 'DynamoDB',
        },
        {
          id: storageId,
          type: 'storage',
          name: 'S3 Bucket',
          position: { x: 700, y: 250 },
          technology: 'S3',
        },
        {
          id: queueId,
          type: 'queue',
          name: 'SQS',
          position: { x: 700, y: 350 },
          technology: 'SQS',
        },
      ];

      const connections: SystemConnection[] = [
        {
          id: generateId(),
          sourceId: cdnId,
          targetId: gatewayId,
          connectionType: 'http',
          dataFlow: 'bidirectional',
        },
        {
          id: generateId(),
          sourceId: gatewayId,
          targetId: authFnId,
          connectionType: 'http',
          dataFlow: 'bidirectional',
          label: 'Authorize',
        },
        {
          id: generateId(),
          sourceId: gatewayId,
          targetId: apiFnId,
          connectionType: 'http',
          dataFlow: 'bidirectional',
        },
        {
          id: generateId(),
          sourceId: apiFnId,
          targetId: dbId,
          connectionType: 'http',
          dataFlow: 'bidirectional',
        },
        {
          id: generateId(),
          sourceId: apiFnId,
          targetId: storageId,
          connectionType: 'http',
          dataFlow: 'bidirectional',
        },
        {
          id: generateId(),
          sourceId: apiFnId,
          targetId: queueId,
          connectionType: 'async',
          dataFlow: 'request',
        },
        {
          id: generateId(),
          sourceId: queueId,
          targetId: processFnId,
          connectionType: 'async',
          dataFlow: 'request',
          label: 'Trigger',
        },
      ];

      return {
        name: 'Serverless Architecture',
        description: 'AWS serverless stack',
        components,
        connections,
        annotations: [],
        settings: DEFAULT_DIAGRAM_SETTINGS,
      };
    },
  },
  {
    id: 'data-pipeline',
    name: 'Data Pipeline',
    description: 'ETL/ELT data pipeline with streaming and batch processing',
    category: 'data',
    icon: '🔄',
    createDiagram: () => {
      const sourceDbId = generateId();
      const cdcId = generateId();
      const streamId = generateId();
      const processorId = generateId();
      const warehouseId = generateId();
      const analyticsId = generateId();

      const components: SystemComponent[] = [
        {
          id: sourceDbId,
          type: 'database',
          name: 'Source DB',
          position: { x: 100, y: 200 },
          technology: 'PostgreSQL',
        },
        {
          id: cdcId,
          type: 'service',
          name: 'CDC Connector',
          position: { x: 300, y: 200 },
          technology: 'Debezium',
        },
        {
          id: streamId,
          type: 'queue',
          name: 'Kafka',
          position: { x: 500, y: 200 },
          technology: 'Kafka',
        },
        {
          id: processorId,
          type: 'service',
          name: 'Stream Processor',
          position: { x: 700, y: 200 },
          technology: 'Flink',
        },
        {
          id: warehouseId,
          type: 'database',
          name: 'Data Warehouse',
          position: { x: 900, y: 150 },
          technology: 'Snowflake',
        },
        {
          id: analyticsId,
          type: 'external',
          name: 'BI Tool',
          position: { x: 900, y: 280 },
          technology: 'Looker',
        },
      ];

      const connections: SystemConnection[] = [
        {
          id: generateId(),
          sourceId: sourceDbId,
          targetId: cdcId,
          connectionType: 'tcp',
          dataFlow: 'request',
          label: 'CDC',
        },
        {
          id: generateId(),
          sourceId: cdcId,
          targetId: streamId,
          connectionType: 'pubsub',
          dataFlow: 'request',
        },
        {
          id: generateId(),
          sourceId: streamId,
          targetId: processorId,
          connectionType: 'pubsub',
          dataFlow: 'request',
        },
        {
          id: generateId(),
          sourceId: processorId,
          targetId: warehouseId,
          connectionType: 'tcp',
          dataFlow: 'request',
          label: 'Load',
        },
        {
          id: generateId(),
          sourceId: warehouseId,
          targetId: analyticsId,
          connectionType: 'http',
          dataFlow: 'response',
          label: 'Query',
        },
      ];

      return {
        name: 'Data Pipeline',
        description: 'Real-time data pipeline with CDC',
        components,
        connections,
        annotations: [],
        settings: DEFAULT_DIAGRAM_SETTINGS,
      };
    },
  },
  {
    id: 'three-tier',
    name: 'Three-Tier Web App',
    description: 'Classic three-tier architecture with load balancing',
    category: 'monolith',
    icon: '🏢',
    createDiagram: () => {
      const clientId = generateId();
      const cdnId = generateId();
      const lbId = generateId();
      const app1Id = generateId();
      const app2Id = generateId();
      const cacheId = generateId();
      const dbPrimaryId = generateId();
      const dbReplicaId = generateId();

      const components: SystemComponent[] = [
        {
          id: clientId,
          type: 'client',
          name: 'Browser',
          position: { x: 100, y: 200 },
          technology: 'Web',
        },
        {
          id: cdnId,
          type: 'cdn',
          name: 'CDN',
          position: { x: 250, y: 100 },
          technology: 'CloudFront',
        },
        {
          id: lbId,
          type: 'load-balancer',
          name: 'Load Balancer',
          position: { x: 250, y: 250 },
          technology: 'ALB',
        },
        {
          id: app1Id,
          type: 'service',
          name: 'App Server 1',
          position: { x: 450, y: 150 },
          technology: 'Node.js',
          scaling: { replicas: 2 },
        },
        {
          id: app2Id,
          type: 'service',
          name: 'App Server 2',
          position: { x: 450, y: 300 },
          technology: 'Node.js',
          scaling: { replicas: 2 },
        },
        {
          id: cacheId,
          type: 'cache',
          name: 'Redis Cache',
          position: { x: 650, y: 100 },
          technology: 'Redis',
        },
        {
          id: dbPrimaryId,
          type: 'database',
          name: 'Primary DB',
          position: { x: 650, y: 250 },
          technology: 'PostgreSQL',
        },
        {
          id: dbReplicaId,
          type: 'database',
          name: 'Read Replica',
          position: { x: 650, y: 380 },
          technology: 'PostgreSQL',
        },
      ];

      const connections: SystemConnection[] = [
        {
          id: generateId(),
          sourceId: clientId,
          targetId: cdnId,
          connectionType: 'http',
          dataFlow: 'bidirectional',
          label: 'Static',
        },
        {
          id: generateId(),
          sourceId: clientId,
          targetId: lbId,
          connectionType: 'http',
          dataFlow: 'bidirectional',
          label: 'API',
        },
        {
          id: generateId(),
          sourceId: lbId,
          targetId: app1Id,
          connectionType: 'http',
          dataFlow: 'bidirectional',
        },
        {
          id: generateId(),
          sourceId: lbId,
          targetId: app2Id,
          connectionType: 'http',
          dataFlow: 'bidirectional',
        },
        {
          id: generateId(),
          sourceId: app1Id,
          targetId: cacheId,
          connectionType: 'tcp',
          dataFlow: 'bidirectional',
        },
        {
          id: generateId(),
          sourceId: app2Id,
          targetId: cacheId,
          connectionType: 'tcp',
          dataFlow: 'bidirectional',
        },
        {
          id: generateId(),
          sourceId: app1Id,
          targetId: dbPrimaryId,
          connectionType: 'tcp',
          dataFlow: 'bidirectional',
        },
        {
          id: generateId(),
          sourceId: app2Id,
          targetId: dbPrimaryId,
          connectionType: 'tcp',
          dataFlow: 'bidirectional',
        },
        {
          id: generateId(),
          sourceId: dbPrimaryId,
          targetId: dbReplicaId,
          connectionType: 'tcp',
          dataFlow: 'request',
          label: 'Replication',
        },
      ];

      return {
        name: 'Three-Tier Web Application',
        description: 'Classic scalable web architecture',
        components,
        connections,
        annotations: [],
        settings: DEFAULT_DIAGRAM_SETTINGS,
      };
    },
  },
];

/**
 * Get template by ID
 */
export function getTemplate(id: string): DiagramTemplate | undefined {
  return DIAGRAM_TEMPLATES.find((t) => t.id === id);
}

/**
 * Get templates by category
 */
export function getTemplatesByCategory(category: DiagramTemplate['category']): DiagramTemplate[] {
  return DIAGRAM_TEMPLATES.filter((t) => t.category === category);
}
